import FormField from "./FormField";
import type { CheckoutForm } from "@/types";
import type { CheckoutErrors } from "@/lib/commerce";

interface StepDadosProps {
  form: CheckoutForm;
  errors: CheckoutErrors;
  onChange: (name: string, value: string | boolean) => void;
  onOpenPrivacy: () => void;
}

export default function StepDados({ form, errors, onChange, onOpenPrivacy }: StepDadosProps) {
  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="font-heading text-2xl text-foreground-900">Seus dados</h2>
        <p className="mt-1 text-sm text-foreground-600">
          Assim conseguimos falar com você e organizar o seu pedido com carinho.
        </p>
      </div>

      <FormField
        label="Nome completo"
        name="name"
        value={form.name}
        onChange={(name, value) => onChange(name, value)}
        error={errors.name}
        placeholder="Como podemos te chamar?"
        autoComplete="name"
      />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <FormField
          label="WhatsApp"
          name="phone"
          value={form.phone}
          onChange={(name, value) => onChange(name, value)}
          error={errors.phone}
          placeholder="(62) 99999-9999"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          hint="Com DDD, para combinarmos os detalhes."
        />
        <FormField
          label="E-mail"
          name="email"
          value={form.email}
          onChange={(name, value) => onChange(name, value)}
          error={errors.email}
          placeholder="seu@email.com"
          type="email"
          inputMode="email"
          autoComplete="email"
          optional
        />
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-[2fr_1fr]">
        <FormField
          label="Cidade"
          name="city"
          value={form.city}
          onChange={(name, value) => onChange(name, value)}
          error={errors.city}
          placeholder="Sua cidade"
          autoComplete="address-level2"
        />
        <FormField
          label="Estado"
          name="state"
          value={form.state}
          onChange={(name, value) => onChange(name, value.toUpperCase().slice(0, 2))}
          error={errors.state}
          placeholder="GO"
          maxLength={2}
          autoComplete="address-level1"
        />
      </div>

      <label className="flex flex-col gap-1.5">
        <span className="flex items-center gap-2 text-sm text-foreground-700">
          Observações
          <span className="text-[11px] uppercase tracking-[0.14em] text-foreground-400">opcional</span>
        </span>
        <textarea
          name="notes"
          value={form.notes}
          onChange={(event) => onChange("notes", event.target.value)}
          maxLength={500}
          rows={3}
          placeholder="Quer contar algo pra gente? Ex.: preferência de horário, embalagem de presente..."
          className="resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
        />
        <span className="text-right text-[11px] text-foreground-400">{form.notes.length}/500</span>
      </label>

      <div className="rounded-lg border border-background-300 bg-background-100 p-4">
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            name="consent"
            checked={form.consent}
            onChange={(event) => onChange("consent", event.target.checked)}
            className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer rounded border-background-400 accent-primary-500"
          />
          <span className="text-sm leading-relaxed text-foreground-700">
            Autorizo o uso destes dados para o atendimento, confirmação e acompanhamento deste
            pedido.{" "}
            <button
              type="button"
              onClick={onOpenPrivacy}
              className="cursor-pointer whitespace-nowrap font-medium text-primary-700 underline underline-offset-4"
            >
              Ver política de privacidade
            </button>
          </span>
        </label>
        {errors.consent ? (
          <p className="mt-2 flex items-center gap-2 text-xs text-accent-800">
            <i className="ri-error-warning-line text-sm" />
            {errors.consent}
          </p>
        ) : null}
      </div>
    </div>
  );
}